import { useRef, useState } from "react";
import { pencereSuresiMetni, yerelAn, yerelParcalar } from "@koclukpanel/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton } from "@koclukpanel/ui";
import { ApiHatasi, denemeGuncelle, type DenemeSatiri } from "./api";

/**
 * CEVAP PENCERESİNİ DÜZENLE — F-EXM-01b
 *
 * ================================================================
 * NEDEN VAR
 * ================================================================
 * `PATCH /denemeler/:id` pencereyi (`baslangic` / `bitis`) değiştirmeyi
 * zaten destekliyordu; ekranda yalnız kitapçık PDF'i düzenlenebiliyordu.
 * Yanlış girilen ya da uzaması gereken bir pencere için tek yol
 * API'ye elle istek atmaktı.
 *
 * ================================================================
 * KARAR SUNUCUDA, EKRAN ÖNCEDEN SÖYLÜYOR
 * ================================================================
 * Kural `apps/api/.../deneme-pencere.ts`'te (cevap anahtarı pencere
 * kapanınca öğrencilere açıldığı için):
 *  - KAPANMIŞ pencere yeniden açılamaz  -> alanlar kapalı + açıklama;
 *  - AÇIK pencerenin başlangıcı değişmez -> başlangıç kapalı, bitiş
 *    yalnız şu andan sonrasına;
 *  - HENÜZ AÇILMAMIŞ pencere serbest.
 * Ekran bunu kullanıcı reddedilmeyi beklemeden görsün diye uyguluyor;
 * kural gevşediğinde ya da saat ekranla sunucu arasında kaydığında
 * (sunucu satır kilidi altında KENDİ saatiyle karar veriyor) sunucunun
 * mesajı olduğu gibi gösteriliyor — ekran ikinci bir kural motoru değil.
 *
 * ================================================================
 * YALNIZ DEĞİŞEN ALAN GÖNDERİLİYOR
 * ================================================================
 * Form dakikaya yuvarlıyor ("HH:MM"); mevcut an ise saniyeli olabilir
 * (eski istemci bitişi 23:59:59 yazıyordu). Dokunulmamış alanı da
 * yuvarlayıp göndersek sunucu "başlangıç değişti" diye açık pencerede
 * `BASLANGIC_DEGISMEZ` ile reddederdi. Bu yüzden her alanın taslağı
 * mevcut değerle karşılaştırılıyor ve yalnız farklı olan gidiyor.
 *
 * Görünürlük: çağıran `deneme.duzenleyebilir` ipucuyla çiziyor (PDF
 * düzenlemeyle AYNI koşul, rol adı yok); karar yine sunucuda.
 */

const SAAT_BICIMI = /^\d{2}:\d{2}$/;

type PencereDurumu = "kapandi" | "acik" | "bekliyor";

function durumBul(deneme: DenemeSatiri, simdi: number): PencereDurumu {
  if (new Date(deneme.bitis).getTime() <= simdi) return "kapandi";
  if (new Date(deneme.baslangic).getTime() <= simdi) return "acik";
  return "bekliyor";
}

interface Taslak {
  baslangicGun: string;
  baslangicSaat: string;
  bitisGun: string;
  bitisSaat: string;
}

function taslakCikar(deneme: DenemeSatiri): Taslak {
  const b = yerelParcalar(deneme.baslangic);
  const s = yerelParcalar(deneme.bitis);
  return { baslangicGun: b.gun, baslangicSaat: b.saat, bitisGun: s.gun, bitisSaat: s.saat };
}

export function DenemePencereDuzenle({
  deneme,
  mevcutMetin,
}: {
  deneme: DenemeSatiri;
  /** `pencereMetni(...)` — liste ve form önizlemesiyle AYNI metin. */
  mevcutMetin: string;
}) {
  const qc = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [taslak, setTaslak] = useState<Taslak>(() => taslakCikar(deneme));

  /*
    ÇİFT TIKLAMA: `isPending` bir sonraki çizimde `true` oluyor; iki hızlı
    tıklama o çizimden ÖNCE gelirse ikisi de geçerdi. Aynı işlemin ikinci
    isteği "başlangıç değişti" gibi sahte bir ret doğurabilir, o yüzden
    kapı çizimden bağımsız bir ref.
  */
  const gonderiliyor = useRef(false);

  const kaydet = useMutation({
    mutationFn: (govde: { baslangic?: string; bitis?: string }) => denemeGuncelle(deneme.id, govde),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
      setAcik(false);
    },
    onSettled: () => {
      gonderiliyor.current = false;
    },
  });

  const simdi = Date.now();
  const durum = durumBul(deneme, simdi);
  const mevcut = taslakCikar(deneme);

  const baslangicDegisti =
    taslak.baslangicGun !== mevcut.baslangicGun || taslak.baslangicSaat !== mevcut.baslangicSaat;
  const bitisDegisti = taslak.bitisGun !== mevcut.bitisGun || taslak.bitisSaat !== mevcut.bitisSaat;

  const baslangicGecerli = taslak.baslangicGun.length > 0 && SAAT_BICIMI.test(taslak.baslangicSaat);
  const bitisGecerli = taslak.bitisGun.length > 0 && SAAT_BICIMI.test(taslak.bitisSaat);

  // Dokunulmamış uç, mevcut gerçek anı korur (saniye/milisaniye kaybolmaz).
  const baslangicAni = baslangicDegisti
    ? baslangicGecerli
      ? yerelAn(taslak.baslangicGun, taslak.baslangicSaat)
      : null
    : new Date(deneme.baslangic);
  const bitisAni = bitisDegisti
    ? bitisGecerli
      ? yerelAn(taslak.bitisGun, taslak.bitisSaat)
      : null
    : new Date(deneme.bitis);

  const pencereTers =
    baslangicAni !== null && bitisAni !== null && bitisAni.getTime() <= baslangicAni.getTime();
  const bitisGecmiste =
    durum === "acik" && bitisDegisti && bitisAni !== null && bitisAni.getTime() <= simdi;

  const bitisHatasi = pencereTers
    ? "Bitiş, başlangıçtan sonra olmalı."
    : bitisGecmiste
      ? "Açık pencerenin bitişi şu andan sonra olmalı."
      : undefined;

  const sure =
    baslangicAni && bitisAni && !pencereTers ? pencereSuresiMetni(baslangicAni, bitisAni) : null;

  const kaydedilebilir =
    durum !== "kapandi" &&
    (baslangicDegisti || bitisDegisti) &&
    baslangicAni !== null &&
    bitisAni !== null &&
    !pencereTers &&
    !bitisGecmiste;

  const kaydetTikla = () => {
    if (!kaydedilebilir || !baslangicAni || !bitisAni || gonderiliyor.current) return;
    gonderiliyor.current = true;
    kaydet.mutate({
      ...(baslangicDegisti && durum === "bekliyor"
        ? { baslangic: baslangicAni.toISOString() }
        : {}),
      ...(bitisDegisti ? { bitis: bitisAni.toISOString() } : {}),
    });
  };

  const ac = () => {
    setTaslak(taslakCikar(deneme));
    kaydet.reset();
    setAcik(true);
  };

  if (!acik) {
    return (
      // Mevcut pencere metni kartın üstündeki satırda zaten duruyor; burada
      // tekrarlamıyoruz, düzenleme açılınca yeniden gösteriliyor.
      <div>
        <Buton gorunum="sessiz" onClick={ac}>
          Cevap penceresini düzenle
        </Buton>
      </div>
    );
  }

  const baslangicKapali = durum !== "bekliyor";
  const bitisKapali = durum === "kapandi";

  return (
    <div className="flex flex-col gap-3">
      <p className="text-notr-700 text-sm">
        <span className="font-medium">Mevcut pencere:</span> {mevcutMetin}
      </p>

      {durum === "kapandi" && (
        <p role="status" className="text-notr-700 text-sm font-medium">
          Pencere kapandı; cevap anahtarı öğrencilere açıldığı için yeniden açılamaz.
        </p>
      )}
      {durum === "acik" && (
        <p className="text-notr-600 text-sm">
          Pencere açık: yalnız bitişi değiştirebilirsiniz ve bitiş şu andan sonra olmalı.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid grid-cols-[1fr_7.5rem] items-start gap-2">
          <Alan
            etiket="Cevap girişi başlangıcı"
            type="date"
            value={taslak.baslangicGun}
            disabled={baslangicKapali}
            onChange={(e) => setTaslak({ ...taslak, baslangicGun: e.target.value })}
            yardim={durum === "acik" ? "Açık pencerenin başlangıcı değişmez." : undefined}
          />
          <Alan
            etiket="Başlangıç saati"
            type="time"
            value={taslak.baslangicSaat}
            disabled={baslangicKapali}
            onChange={(e) => setTaslak({ ...taslak, baslangicSaat: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-[1fr_7.5rem] items-start gap-2">
          <Alan
            etiket="Cevap girişi bitişi"
            type="date"
            value={taslak.bitisGun}
            disabled={bitisKapali}
            onChange={(e) => setTaslak({ ...taslak, bitisGun: e.target.value })}
            hata={bitisHatasi}
            yardim={bitisHatasi ? undefined : "Anahtar öğrenciye bu tarihten sonra açılır."}
          />
          <Alan
            etiket="Bitiş saati"
            type="time"
            value={taslak.bitisSaat}
            disabled={bitisKapali}
            onChange={(e) => setTaslak({ ...taslak, bitisSaat: e.target.value })}
          />
        </div>
        {sure && (
          <p className="text-notr-600 text-sm sm:col-span-2" aria-live="polite">
            Pencere süresi: {sure} (saatler Türkiye saatiyle)
          </p>
        )}
      </div>

      {kaydet.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton yukleniyor={kaydet.isPending} disabled={!kaydedilebilir} onClick={kaydetTikla}>
          Kaydet
        </Buton>
        <Buton gorunum="ikincil" onClick={() => setAcik(false)}>
          Vazgeç
        </Buton>
      </div>
    </div>
  );
}
